// @ts-nocheck

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Route, Switch } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { NotConnected1 } from '../../../assets/img';
import { Target } from '../../../client';
import InfosLabel, { InfosLabelTypes } from '../../../components/InfosLabel';
import Modal from '../../../components/Modal';
import ModalFixedContent from '../../../components/ModalFixed';
import NotFoundView from '../../../components/NotFoundView';
import WinaLoader from '../../../components/WinaLoader';
import { ModalContentSpacing } from '../../../constants/ui/modal';
import { getAppDOMElement } from '../../../DOMElements';
import useRouter from '../../../hooks/useRouter';
import { __ } from '../../../i18n/l10n';
import {
  selectUserConnected,
  selectUserConnecting,
  selectUserDidReceivedUserData,
} from '../../../selectors/user/userPrimary.selector';
import { RoutePatterns } from '../../../types/routes';
import { showWebsiteLoginForm } from '../../../utils';
import RoutesRedirectionFactory from '../../../utils/routes/RoutesRedirectionFactory';
import { claimAllMPRewards } from '../actions/mp';
import MPEnrollmentContainer from '../containers/MPEnrollmentContainer';
import MPFamilyDetailsContainer from '../containers/MPFamilyDetailsContainer';
import { MPModalContext } from '../contexts/MPModalContext';
import { selectIsMPEnabled } from '../selectors/computed/runtime';
import { selectMPGlobalOperationEndDate, selectMPGlobalState, selectMPGlobalStatus } from '../selectors/global';
import { selectMPUserStatus } from '../selectors/user';
import { MPGlobalStatuses } from '../types/global';
import { MPUserStatuses } from '../types/user';

import MPHome from './MPHome';

const StyledWinaLoader = styled(WinaLoader)`
  margin: 0;
  margin-top: 55%;
  transform: translateY(-50%);
`;

const RouterSwitchWrapper = styled.div`
  position: relative;
`;

const StyledInfosLabel = styled(InfosLabel)`
  margin: 0;
  border-radius: 0;
  text-align: center;

  ${Target.__MOBILE__
    ? css`
        position: absolute;
        left: 0;
        width: 100%;
        top: 0;
        z-index: 1;
      `
    : undefined}
`;

type ValidatedMPRouterSwitchProps = {
  className?: string;
  willOperationEndSoon: boolean;
  isOperationEnding: boolean;
};

const ValidatedMPRouterSwitch = ({
  className,
  willOperationEndSoon,
  isOperationEnding,
}: ValidatedMPRouterSwitchProps) => {
  const [bannerElement, setBannerElement] = useState();
  const routerSwitchWrapperRef = useRef();
  const showEndingBanner = useMemo(
    () => isOperationEnding || willOperationEndSoon,
    [isOperationEnding, willOperationEndSoon],
  );

  const bannerLabel = useMemo(() => {
    if (isOperationEnding) {
      return __('This promotion has ended');
    }

    if (willOperationEndSoon) {
      return __('This promotion will end in under 24 hours');
    }
  }, [isOperationEnding, willOperationEndSoon]);

  useLayoutEffect(() => {
    const routerSwitchWrapperElement = routerSwitchWrapperRef.current;

    if (routerSwitchWrapperElement && bannerElement) {
      const { height: bannerHeight } = bannerElement.getBoundingClientRect();

      if (typeof bannerHeight === 'number') {
        if (Target.__MOBILE__) {
          routerSwitchWrapperElement.style.paddingTop = `${bannerHeight}px`;
        } else {
          routerSwitchWrapperElement.style.marginTop = `${bannerHeight}px`;
        }
      }
    }
  }, [bannerElement]);

  useLayoutEffect(() => {
    const scrollElement = Target.__MOBILE__ ? getAppDOMElement() : window;

    const scrollCallback = () => {
      if (bannerElement) {
        bannerElement.style.top = `${scrollElement.scrollTop}px`;
      }
    };

    scrollElement.addEventListener('scroll', scrollCallback);

    return () => {
      scrollElement.removeEventListener('scroll', scrollCallback);
    };
  }, [bannerElement]);

  const modalFixedRender = useCallback(
    () => (
      <StyledInfosLabel
        ref={(ref) => setBannerElement(ref)}
        label={bannerLabel}
        type={isOperationEnding ? InfosLabelTypes.INFOS_YELLOW : InfosLabelTypes.INFOS_BLUE}
        symbol={'!'}
      />
    ),
    [bannerLabel, isOperationEnding],
  );

  const renderEndingBanner = useCallback(() => {
    if (showEndingBanner) {
      if (Target.__MOBILE__) {
        return modalFixedRender();
      }
      return <ModalFixedContent render={modalFixedRender} />;
    }

    return null;
  }, [modalFixedRender, showEndingBanner]);

  return (
    <>
      <RouterSwitchWrapper ref={routerSwitchWrapperRef}>
        {renderEndingBanner()}

        <Switch>
          <Route path={RoutePatterns.MP} exact>
            <MPHome className={className} />
          </Route>
          <Route path={RoutePatterns.MP_ENROLLMENT} exact>
            <MPEnrollmentContainer className={className} />
          </Route>
          <Route path={RoutePatterns.MP_FAMILY_DETAIL} exact>
            <MPFamilyDetailsContainer className={className} />
          </Route>
        </Switch>
      </RouterSwitchWrapper>
    </>
  );
};

const StyledValidatedMPRouterSwitch = styled(ValidatedMPRouterSwitch)``;

type ValidatedMPRouterSwitchContainerProps = {
  className?: string;
};

const ValidatedMPRouterSwitchContainer = ({ className }: ValidatedMPRouterSwitchContainerProps) => {
  const { history } = useRouter();
  const isConnected = useSelector(selectUserConnected);
  const isConnecting = useSelector(selectUserConnecting);
  const MPUserStatus = useSelector(selectMPUserStatus);
  const didReceivedUserData = useSelector(selectUserDidReceivedUserData);
  const isMPEnabled = useSelector(selectIsMPEnabled);
  const MPGlobalState = useSelector(selectMPGlobalState);

  const globalStatus = useSelector(selectMPGlobalStatus);
  const operationEndDate = useSelector(selectMPGlobalOperationEndDate);

  const isConnectedRef = useRef(isConnected);
  const isConnectingRef = useRef(isConnecting);
  const MPUserStatusRef = useRef(MPUserStatus);
  const MPGlobalStateRef = useRef(MPGlobalState);

  useEffect(() => {
    isConnectedRef.current = isConnected;
  }, [isConnected]);

  useEffect(() => {
    isConnectingRef.current = isConnecting;
  }, [isConnecting]);

  useEffect(() => {
    MPUserStatusRef.current = MPUserStatus;
  }, [MPUserStatus]);

  useEffect(() => {
    MPGlobalStateRef.current = MPGlobalState;
  }, [MPGlobalState]);

  useLayoutEffect(() => {
    if (!MPGlobalStateRef.current || (Object.keys(MPGlobalStateRef.current).length === 0 && !isMPEnabled)) {
      history.push(RoutesRedirectionFactory.getHomeRouteConfig());
    }

    if (!isConnectedRef.current && !isConnectingRef.current) {
      if (Target.__MOBILE__) {
        history.push({
          ...RoutesRedirectionFactory.getLoginRouteConfig({
            state: {
              modal: true,
            },
          }),
        });
      } else {
        showWebsiteLoginForm();

        history.push(RoutesRedirectionFactory.getHomeRouteConfig());
      }
    }

    if (didReceivedUserData) {
      if (!MPUserStatusRef.current || MPUserStatusRef.current === MPUserStatuses.OFF) {
        history.push(RoutesRedirectionFactory.getMPEnrollementRouteConfig());
      }
    }
  }, [didReceivedUserData, history, isMPEnabled]);

  if (!isConnected && !isConnecting) {
    return <NotFoundView label={__('You must be logged in to see this page')} imgPath={NotConnected1} />;
  }

  if (didReceivedUserData) {
    return (
      <StyledValidatedMPRouterSwitch
        className={className}
        isOperationEnding={globalStatus === MPGlobalStatuses.ENDING}
        willOperationEndSoon={operationEndDate - Date.now() < 1000 * 3600 * 24}
      />
    );
  }

  return <StyledWinaLoader />;
};

const Wrapper = styled.div`
  --Modal-backButtonBackground: rgb(47, 85, 119);
  --Modal-backButtonOpacity: 1;
`;

const fixedBannerStyle = css`
  pointer-events: none;
`;

type MPRouterSwitchProps = {
  className?: string;
};

const MPRouterSwitch = ({ className = '' }: MPRouterSwitchProps) => {
  const { history } = useRouter();
  const dispatch = useDispatch();
  const [disableScrollView, setDisableScrollView] = useState(false);
  const [notificationUuids, setNotificationUuids] = useState([]);

  const modalContextValue = useMemo(
    () => ({
      disableScrollView,
      setDisableScrollView,
      setNotificationUuids,
    }),
    [disableScrollView, setDisableScrollView],
  );

  const onCloseModal = useCallback(() => {
    if (notificationUuids.length) {
      dispatch(claimAllMPRewards({ uuids: notificationUuids }));
    }

    history.replace(RoutesRedirectionFactory.getHomeRouteConfig());
  }, [dispatch, history, notificationUuids]);

  return (
    <Wrapper className={className}>
      <Switch>
        <Route path={RoutePatterns.MP}>
          {!Target.__MOBILE__ ? (
            <MPModalContext.Provider value={modalContextValue}>
              <Modal
                backgroundColor={'#000000'}
                contentSpacing={ModalContentSpacing.NONE}
                hasOverlay
                closeOnOutsideClick
                closeOnEsc
                onClose={onCloseModal}
                withBorder
                hasCloseButton
                disableScrollView={disableScrollView}
                forbidBackgroundScrolling
                fixedStyle={fixedBannerStyle}
              >
                <ValidatedMPRouterSwitchContainer />
              </Modal>
            </MPModalContext.Provider>
          ) : (
            <ValidatedMPRouterSwitchContainer />
          )}
        </Route>
      </Switch>
    </Wrapper>
  );
};

export default MPRouterSwitch;
